// page-bug.ts — Page de MAJ d'un bug (côté utilisateur).
//
// Formulaire (titre, description, priorité, urgence, état), captures (upload +
// vignettes + suppression), fil de messages (du plus récent au plus ancien,
// ajout + édition de ses propres messages). Les champs lus dans l'appli
// (id, déclarant, version déclarée, version de résolution) sont en lecture seule.

import { definePage, navigateTo } from '../router.js';

function _breadcrumb(current: string): string {
  return `<a href="#bugs" style="color:var(--accent);text-decoration:underline">${esc(t('bugs.breadcrumb.list'))}</a> / ${esc(current)}`;
}

// Ordre des ids de bugs de la liste (filtres + tri), posé par page-bugs dans
// sessionStorage. Sert à proposer « bug précédent / suivant » fidèle à la liste.
const BUGS_ORDER_KEY = 'ornexia:bugs:order';
let _order: number[] | null = null;

// Bornes de saisie, identiques à `savira_common/bug_limits.py` (au-delà, l'API
// répond 422) : posées en `maxlength` pour que la limite se voie avant le refus.
const TITLE_MAX = 200;
const DESC_MAX = 10000;
const MSG_MAX = 5000;

async function _ensureOrder(): Promise<number[]> {
  if (_order) return _order;
  try {
    const raw = sessionStorage.getItem(BUGS_ORDER_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        _order = arr as number[];
        return _order;
      }
    }
  } catch {
    /* sessionStorage indisponible */
  }
  // Accès direct / refresh sans passer par la liste : ordre par défaut de la
  // liste (id décroissant ; bugs résolus exclus comme dans la liste par défaut).
  try {
    const bugs = (await api.getBugs({})) as Array<{ id: number }>;
    _order = bugs.map((b) => b.id);
  } catch {
    _order = [];
  }
  return _order;
}

interface RefItem {
  code: string;
  label: string;
  is_final?: boolean;
}
interface Referentials {
  priorities: RefItem[];
  urgencies: RefItem[];
  states: RefItem[];
}
interface BugImageRef {
  id: number;
  content_type: string;
  url: string;
}
interface BugDetail {
  id: number;
  title: string;
  description: string | null;
  state_code: string;
  priority_code: string;
  urgency_code: string;
  reporter_trigram: string | null;
  version_declared: string;
  resolution_version: string | null;
  is_evolution: boolean;
  created_at: string | null;
  images: BugImageRef[];
}
interface BugMessage {
  id: number;
  author_trigram: string | null;
  body: string;
  is_mine: boolean;
  created_at: string | null;
}
interface BugStateChange {
  id: number;
  author_trigram: string | null;
  old_state_label: string | null;
  new_state_label: string;
  created_at: string | null;
}

function _fmtDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s.endsWith('Z') ? s : s + 'Z');
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('fr-FR');
}

function _opts(items: RefItem[], selected: string): string {
  return items
    .map(
      (i) =>
        `<option value="${esc(i.code)}"${i.code === selected ? ' selected' : ''}>${esc(i.label)}</option>`,
    )
    .join('');
}

function _imagesHtml(bug: BugDetail): string {
  const thumbs = bug.images
    .map(
      (img) => `<div style="position:relative;display:inline-block;margin:0 8px 8px 0">
        <a href="${esc(img.url)}" target="_blank" rel="noopener">
          <img src="${esc(img.url)}" alt="capture" style="height:96px;border:1px solid var(--border);border-radius:4px;display:block"></a>
        <button class="btn btn-danger btn-icon" data-del-img="${img.id}" title="${esc(t('bugs.images.delete'))}"
          style="position:absolute;top:2px;right:2px">✕</button>
      </div>`,
    )
    .join('');
  return `<div style="display:flex;flex-wrap:wrap;align-items:flex-start">${thumbs || `<span style="color:var(--text3);font-style:italic">${esc(t('bugs.images.empty'))}</span>`}</div>`;
}

function _messagesHtml(messages: BugMessage[]): string {
  if (!messages.length)
    return `<p style="color:var(--text3);font-style:italic">${esc(t('bugs.messages.empty'))}</p>`;
  return messages
    .map(
      (m) => `<div class="card" data-msg-id="${m.id}" style="padding:10px;margin-bottom:8px">
        <div style="font-size:11px;color:var(--text3);margin-bottom:4px">
          ${esc(m.author_trigram || '—')} · ${esc(_fmtDate(m.created_at))}
          ${m.is_mine ? `<button class="btn btn-secondary btn-icon" data-edit-msg="${m.id}" title="${esc(t('common.edit'))}" style="color:#147A57;margin-left:6px">✎</button>` : ''}
        </div>
        <div data-msg-body="${m.id}" style="white-space:pre-wrap">${esc(m.body)}</div>
      </div>`,
    )
    .join('');
}

function _historyHtml(history: BugStateChange[]): string {
  if (!history.length)
    return `<p style="color:var(--text3);font-style:italic">${esc(t('bugs.history.empty'))}</p>`;
  return history
    .map(
      (h) => `<div style="font-size:12px;padding:6px 0;border-bottom:1px solid var(--border)">
        <span style="color:var(--text3)">${esc(h.author_trigram || '—')} · ${esc(_fmtDate(h.created_at))}</span><br>
        ${h.old_state_label ? esc(h.old_state_label) + ' → ' : ''}<strong>${esc(h.new_state_label)}</strong>
      </div>`,
    )
    .join('');
}

async function _renderBug(id: number, ref: Referentials): Promise<void> {
  const bug = (await api.getBug(id)) as BugDetail;
  const messages = (await api.getBugMessages(id)) as BugMessage[];
  const history = (await api.getBugStateHistory(id)) as BugStateChange[];

  // Consulté → marque lu (retire ce bug de la pastille « réponses non lues »).
  void api.markBugSeen(id).then(() => window.refreshBugsBadge?.());

  // Navigation « bug précédent / suivant » fidèle à la liste (filtres + tri).
  const order = await _ensureOrder();
  const pos = order.indexOf(id);
  const prevId = pos > 0 ? order[pos - 1] : null;
  const nextId = pos >= 0 && pos < order.length - 1 ? order[pos + 1] : null;
  const navHtml =
    `<button class="btn btn-secondary" id="bg-prev" title="${esc(t('bugs.nav.prev'))}"${prevId == null ? ' disabled' : ''}>‹ ${esc(t('bugs.nav.prev'))}</button>` +
    `<button class="btn btn-secondary" id="bg-next" title="${esc(t('bugs.nav.next'))}" style="margin-left:6px"${nextId == null ? ' disabled' : ''}>› ${esc(t('bugs.nav.next'))}</button>` +
    // #16 : déclarer un nouveau bug sans repasser par la liste.
    `<button class="btn btn-primary" id="bg-new" style="margin-left:6px">${esc(t('bugs.btnNew'))}</button>`;
  setHeader(_breadcrumb(t('bugs.detail.title', { id: bug.id })), navHtml);
  document
    .getElementById('bg-new')
    ?.addEventListener('click', () => navigateTo('bug/:id', { id: 0 }));
  if (prevId != null) {
    const pid = prevId;
    document
      .getElementById('bg-prev')
      ?.addEventListener('click', () => navigateTo('bug/:id', { id: pid }));
  }
  if (nextId != null) {
    const nid = nextId;
    document
      .getElementById('bg-next')
      ?.addEventListener('click', () => navigateTo('bug/:id', { id: nid }));
  }

  setContent(`<div class="card">
    <div style="display:flex;gap:16px;flex-wrap:wrap;font-size:12px;color:var(--text3);margin-bottom:12px">
      <span>${esc(t('bugs.info.reporter'))} : <strong>${esc(bug.reporter_trigram || '—')}</strong></span>
      <span>${esc(t('bugs.info.versionDeclared'))} : <strong>${esc(bug.version_declared)}</strong></span>
      <span>${esc(t('bugs.info.created'))} : <strong>${esc(_fmtDate(bug.created_at))}</strong></span>
      <span>${esc(t('bugs.info.resolution'))} : <strong>${esc(bug.resolution_version || '—')}</strong></span>
    </div>
    <div class="form-group"><label>${esc(t('bugs.field.title'))}</label>
      <input type="text" id="bg-title" maxlength="${TITLE_MAX}" value="${esc(bug.title)}" placeholder="${esc(t('bugs.field.titlePlaceholder'))}"></div>
    <div class="form-group"><label>${esc(t('bugs.field.desc'))}</label>
      <textarea id="bg-desc" rows="8" maxlength="${DESC_MAX}" placeholder="${esc(t('bugs.field.descPlaceholder'))}">${esc(bug.description || '')}</textarea></div>
    <label style="display:flex;align-items:center;gap:6px;margin:0 0 8px">
      <input type="checkbox" id="bg-evolution"${bug.is_evolution ? ' checked' : ''}> ${esc(t('bugs.field.isEvolution'))}</label>
    <div style="display:flex;gap:12px;flex-wrap:wrap">
      <div class="form-group" style="flex:1;min-width:160px"><label>${esc(t('bugs.field.priority'))}</label>
        <select id="bg-priority">${_opts(ref.priorities, bug.priority_code)}</select></div>
      <div class="form-group" style="flex:1;min-width:160px"><label>${esc(t('bugs.field.urgency'))}</label>
        <select id="bg-urgency">${_opts(ref.urgencies, bug.urgency_code)}</select></div>
      <div class="form-group" style="flex:1;min-width:160px"><label>${esc(t('bugs.field.state'))}</label>
        <select id="bg-state">${_opts(ref.states, bug.state_code)}</select></div>
    </div>
    <div style="text-align:right;margin-top:14px">
      <button class="btn btn-neutral" id="bg-cancel">${esc(t('common.cancel'))}</button>
      <button class="btn btn-primary" id="bg-save" style="margin-left:8px">${esc(t('common.save'))}</button></div>

    <hr style="margin:16px 0;border:none;border-top:1px solid var(--border)">
    <h3 style="font-size:13px;margin-bottom:8px">${esc(t('bugs.images.title'))}</h3>
    <div id="bg-images">${_imagesHtml(bug)}</div>
    <div class="form-group" style="margin-top:8px"><label>${esc(t('bugs.images.add'))}</label>
      <input type="file" id="bg-image-input" accept="image/png,image/jpeg,image/webp"></div>

    <hr style="margin:16px 0;border:none;border-top:1px solid var(--border)">
    <h3 style="font-size:13px;margin-bottom:8px">${esc(t('bugs.messages.title'))}</h3>
    <div class="form-group">
      <textarea id="bg-new-msg" rows="3" maxlength="${MSG_MAX}" placeholder="${esc(t('bugs.messages.placeholder'))}"></textarea>
      <div style="text-align:right;margin-top:4px"><button class="btn btn-secondary" id="bg-add-msg">${esc(t('bugs.messages.add'))}</button></div>
    </div>
    <div id="bg-messages">${_messagesHtml(messages)}</div>

    <hr style="margin:16px 0;border:none;border-top:1px solid var(--border)">
    <h3 style="font-size:13px;margin-bottom:8px">${esc(t('bugs.history.title'))}</h3>
    <div id="bg-history">${_historyHtml(history)}</div>
  </div>`);

  document.getElementById('bg-cancel')?.addEventListener('click', () => {
    location.hash = '#bugs';
  });

  document.getElementById('bg-save')?.addEventListener('click', () => {
    const title = (document.getElementById('bg-title') as HTMLInputElement | null)?.value.trim();
    if (!title) {
      toast(t('bugs.toast.titleRequired'), 'error');
      return;
    }
    const description = (document.getElementById('bg-desc') as HTMLTextAreaElement | null)?.value;
    // Titre et description ne partent que s'ils ont changé : un bug déclaré
    // avant les bornes de saisie (2026-09-25) peut les dépasser, et l'API
    // refuserait alors un simple changement d'état ou de priorité (422).
    const payload: Record<string, unknown> = {
      priority_code: (document.getElementById('bg-priority') as HTMLSelectElement | null)?.value,
      urgency_code: (document.getElementById('bg-urgency') as HTMLSelectElement | null)?.value,
      state_code: (document.getElementById('bg-state') as HTMLSelectElement | null)?.value,
      is_evolution:
        (document.getElementById('bg-evolution') as HTMLInputElement | null)?.checked ?? false,
    };
    if (title !== bug.title) payload['title'] = title;
    if ((description ?? '') !== (bug.description ?? '')) payload['description'] = description;
    void (async () => {
      try {
        await api.updateBug(id, payload);
        toast(t('bugs.toast.saved'));
        // Re-render : reflète l'état persisté + la nouvelle ligne d'historique
        // si l'état a changé.
        await _renderBug(id, ref);
      } catch (e: unknown) {
        toast(e instanceof Error ? e.message : t('common.error'), 'error');
      }
    })();
  });

  const fileInput = document.getElementById('bg-image-input') as HTMLInputElement | null;
  fileInput?.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    void (async () => {
      try {
        await api.uploadBugImage(id, file);
        toast(t('bugs.toast.imageAdded'));
        await _renderBug(id, ref);
      } catch (e: unknown) {
        toast(e instanceof Error ? e.message : t('common.error'), 'error');
      }
    })();
  });

  document.getElementById('bg-images')?.addEventListener('click', (e: Event) => {
    const del = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-del-img]');
    if (!del) return;
    const imgId = parseInt(del.dataset['delImg'] || '0');
    void (async () => {
      try {
        await api.deleteBugImage(id, imgId);
        await _renderBug(id, ref);
      } catch (err: unknown) {
        toast(err instanceof Error ? err.message : t('common.error'), 'error');
      }
    })();
  });

  document.getElementById('bg-add-msg')?.addEventListener('click', () => {
    const ta = document.getElementById('bg-new-msg') as HTMLTextAreaElement | null;
    const body = ta?.value.trim();
    if (!body) {
      toast(t('bugs.toast.messageEmpty'), 'error');
      return;
    }
    void (async () => {
      try {
        await api.addBugMessage(id, body);
        toast(t('bugs.toast.messageAdded'));
        await _renderBug(id, ref);
      } catch (e: unknown) {
        toast(e instanceof Error ? e.message : t('common.error'), 'error');
      }
    })();
  });

  document.getElementById('bg-messages')?.addEventListener('click', (e: Event) => {
    const edit = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-edit-msg]');
    if (!edit) return;
    const msgId = parseInt(edit.dataset['editMsg'] || '0');
    const msg = messages.find((m) => m.id === msgId);
    if (!msg) return;
    openModal(
      t('bugs.messages.editTitle'),
      `<div class="form-group"><textarea id="em-body" rows="4" maxlength="${MSG_MAX}">${esc(msg.body)}</textarea></div>`,
      async () => {
        const body = (
          document.getElementById('em-body') as HTMLTextAreaElement | null
        )?.value.trim();
        if (!body) {
          toast(t('bugs.toast.messageEmpty'), 'error');
          return;
        }
        try {
          await api.updateBugMessage(id, msgId, body);
          closeModal();
          toast(t('bugs.toast.messageEdited'));
          await _renderBug(id, ref);
        } catch (err: unknown) {
          toast(err instanceof Error ? err.message : t('common.error'), 'error');
        }
      },
      t('common.save'),
    );
  });
}

// Formulaire de création (id sentinelle 0) — le bug n'existe pas encore en base.
// Pas de sections captures / messages / historique (elles nécessitent un id) :
// elles deviennent disponibles après le 1er enregistrement, sur la fiche normale.
function _renderNewBug(ref: Referentials): void {
  // #16 : CTA « Nouveau bug + » aussi sur l'écran de création (réinitialise le
  // formulaire pour enchaîner une nouvelle déclaration sans repasser par la liste).
  setHeader(
    _breadcrumb(t('bugs.new.pageTitle')),
    `<button class="btn btn-primary" id="bg-new">${esc(t('bugs.btnNew'))}</button>`,
  );
  document.getElementById('bg-new')?.addEventListener('click', () => _renderNewBug(ref));
  setContent(`<div class="card">
    <div class="form-group"><label>${esc(t('bugs.field.title'))}</label>
      <input type="text" id="bg-title" maxlength="${TITLE_MAX}" value="" placeholder="${esc(t('bugs.field.titlePlaceholder'))}"></div>
    <div class="form-group"><label>${esc(t('bugs.field.desc'))}</label>
      <textarea id="bg-desc" rows="8" maxlength="${DESC_MAX}" placeholder="${esc(t('bugs.field.descPlaceholder'))}"></textarea></div>
    <label style="display:flex;align-items:center;gap:6px;margin:0 0 8px">
      <input type="checkbox" id="bg-evolution"> ${esc(t('bugs.field.isEvolution'))}</label>
    <div style="display:flex;gap:12px;flex-wrap:wrap">
      <div class="form-group" style="flex:1;min-width:160px"><label>${esc(t('bugs.field.priority'))}</label>
        <select id="bg-priority">${_opts(ref.priorities, 'normal')}</select></div>
      <div class="form-group" style="flex:1;min-width:160px"><label>${esc(t('bugs.field.urgency'))}</label>
        <select id="bg-urgency">${_opts(ref.urgencies, 'normale')}</select></div>
    </div>
    <div style="text-align:right;margin-top:14px">
      <button class="btn btn-neutral" id="bg-cancel">${esc(t('common.cancel'))}</button>
      <button class="btn btn-primary" id="bg-save" style="margin-left:8px">${esc(t('common.save'))}</button></div>
  </div>`);

  document.getElementById('bg-cancel')?.addEventListener('click', () => {
    location.hash = '#bugs';
  });

  document.getElementById('bg-save')?.addEventListener('click', () => {
    const title = (document.getElementById('bg-title') as HTMLInputElement | null)?.value.trim();
    if (!title) {
      toast(t('bugs.toast.titleRequired'), 'error');
      return;
    }
    const payload = {
      title,
      description: (document.getElementById('bg-desc') as HTMLTextAreaElement | null)?.value,
      priority_code: (document.getElementById('bg-priority') as HTMLSelectElement | null)?.value,
      urgency_code: (document.getElementById('bg-urgency') as HTMLSelectElement | null)?.value,
      is_evolution:
        (document.getElementById('bg-evolution') as HTMLInputElement | null)?.checked ?? false,
    };
    void (async () => {
      try {
        const created = (await api.createBug(payload)) as { id: number };
        toast(t('bugs.toast.saved'));
        navigateTo('bug/:id', { id: created.id });
      } catch (e: unknown) {
        toast(e instanceof Error ? e.message : t('common.error'), 'error');
      }
    })();
  });
}

pages.bugDetail = definePage<'bug/:id'>(async ({ id }) => {
  // Re-lit l'ordre de la liste à chaque entrée (les filtres ont pu changer).
  _order = null;
  const isNew = !id; // id sentinelle 0 → formulaire de création (bug #41)
  const headTitle = isNew ? t('bugs.new.pageTitle') : t('bugs.detail.title', { id });
  setHeader(_breadcrumb(headTitle), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const ref = (await api.getBugReferentials()) as Referentials;
    if (isNew) {
      _renderNewBug(ref);
    } else {
      await _renderBug(id, ref);
    }
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
});

export {};
